import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Archive, Search, LayoutGrid, Kanban, GripVertical, ArrowUpDown } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import ProjectCard from "../components/ProjectCard";
import KanbanBoard from "../components/KanbanBoard";
import { AttentionTiles } from "../components/AttentionTiles";
import CreateProjectModal from "./CreateProjectModal";

export default function AdminDashboard() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("dashboard_view") || "grid");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("dashboard_sort") || "last_updated");
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const saveReorderTimer = useRef(null);

  const switchView = (v) => { setView(v); localStorage.setItem("dashboard_view", v); };
  const switchSort = (v) => { setSortBy(v); localStorage.setItem("dashboard_sort", v); };

  const [attentionData, setAttentionData] = useState(null);
  const [attentionFilter, setAttentionFilter] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ps }, { data: cs }] = await Promise.all([
        api.get("/projects", { params: { archived: showArchived } }),
        api.get("/clients"),
      ]);
      setProjects(ps);
      setClients(cs);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  const loadAttention = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/attention");
      setAttentionData(data);
    } catch (_e) { /* best-effort */ }
  }, []);

  const handleStatusChange = async (projectId, newStatus) => {
    setProjects((prev) =>
      prev.map((p) => p.id === projectId ? { ...p, current_status: newStatus, is_on_hold: newStatus === "on_hold" } : p)
    );
    try {
      await api.post(`/projects/${projectId}/status`, { status: newStatus });
      loadAttention();
    } catch (_e) { load(); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAttention(); }, [loadAttention]);

  const filtered = useMemo(() => {
    let list = projects.filter((p) => {
      if (query) {
        const q = query.toLowerCase();
        return (
          (p.project_name || "").toLowerCase().includes(q) ||
          (p.project_reference || "").toLowerCase().includes(q) ||
          (p.client_name || "").toLowerCase().includes(q) ||
          (p.address || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
    // Attention tile filter
    if (attentionFilter && attentionData?.[attentionFilter]) {
      const ids = new Set(attentionData[attentionFilter].map((p) => p.id));
      list = list.filter((p) => ids.has(p.id));
    }
    // Sort
    switch (sortBy) {
      case "manual":
        return [...list].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      case "name":
        return [...list].sort((a, b) => (a.project_name || "").localeCompare(b.project_name || ""));
      case "due_date":
        return [...list].sort((a, b) => {
          if (!a.due_date && !b.due_date) return 0;
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(a.due_date) - new Date(b.due_date);
        });
      case "last_updated":
      default:
        return [...list].sort((a, b) =>
          new Date(b.last_activity_at || b.updated_at || 0) - new Date(a.last_activity_at || a.updated_at || 0)
        );
    }
  }, [projects, query, attentionFilter, attentionData, sortBy]);

  const handleGridReorder = useCallback((fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const reordered = [...filtered];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    // Assign new sort_order values
    const updates = reordered.map((p, idx) => ({ id: p.id, sort_order: idx }));
    // Optimistically update local state
    setProjects((prev) => {
      const orderMap = Object.fromEntries(updates.map((u) => [u.id, u.sort_order]));
      return prev.map((p) => orderMap[p.id] !== undefined ? { ...p, sort_order: orderMap[p.id] } : p);
    });
    // Debounced persist
    clearTimeout(saveReorderTimer.current);
    saveReorderTimer.current = setTimeout(() => {
      api.patch("/projects/reorder", { updates }).catch(() => {});
    }, 600);
  }, [filtered]);

  return (
    <div className="max-w-7xl mx-auto px-8 sm:px-12 py-12" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-2">Practice overview</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-white">
            Projects
          </h1>
          <div className="mt-2 text-sm text-[color:var(--atech-text-dim)]">
            {projects.length} {showArchived ? "archived" : "active"} project{projects.length === 1 ? "" : "s"}
            {clients.length ? ` · ${clients.length} client${clients.length === 1 ? "" : "s"}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          {/* Sort by */}
          <div className="flex items-center gap-1.5">
            <ArrowUpDown size={12} className="text-[color:var(--atech-text-muted)]" />
            <select
              value={sortBy}
              onChange={(e) => switchSort(e.target.value)}
              className="text-xs px-2.5 py-1.5 rounded-[2px] bg-transparent border border-white/10 text-[color:var(--atech-text-dim)] focus:outline-none focus:border-[color:var(--atech-copper)] cursor-pointer"
              data-testid="sort-select"
            >
              <option value="last_updated">Last updated</option>
              <option value="manual">Manual order</option>
              <option value="name">Name A–Z</option>
              <option value="due_date">Due date</option>
            </select>
          </div>

          {/* Grid / Kanban toggle */}
          <div className="flex items-center border border-white/10 rounded-[2px] overflow-hidden" data-testid="view-toggle">
            <button
              onClick={() => switchView("grid")}
              title="Grid view"
              className={`px-3 py-2.5 transition-colors ${
                view === "grid"
                  ? "bg-white/8 text-white"
                  : "text-[color:var(--atech-text-muted)] hover:text-white"
              }`}
              data-testid="view-grid-btn"
            >
              <LayoutGrid size={15} />
            </button>
            <div className="w-px h-5 bg-white/10" />
            <button
              onClick={() => switchView("kanban")}
              title="Kanban view"
              className={`px-3 py-2.5 transition-colors ${
                view === "kanban"
                  ? "bg-white/8 text-white"
                  : "text-[color:var(--atech-text-muted)] hover:text-white"
              }`}
              data-testid="view-kanban-btn"
            >
              <Kanban size={15} />
            </button>
          </div>

          <button
            onClick={() => setShowArchived((v) => !v)}
            className={`text-xs border rounded-[2px] px-4 py-2.5 transition-colors flex items-center gap-2 ${
              showArchived
                ? "bg-white/5 text-white border-white/20"
                : "border-white/10 text-[color:var(--atech-text-dim)] hover:text-white"
            }`}
            data-testid="toggle-archived"
          >
            <Archive size={14} />
            {showArchived ? "Active projects" : "Archived"}
          </button>
          <button
            onClick={() => setOpenCreate(true)}
            className="btn-copper flex items-center gap-2 text-sm"
            data-testid="new-project-button"
          >
            <Plus size={16} /> New project
          </button>
        </div>
      </div>

      {/* Needs-attention inbox */}
      <AttentionTiles
        data={attentionData}
        activeKey={attentionFilter}
        onSelect={(key) => { setAttentionFilter(key); }}
      />

      {/* Search bar */}
      <div className="card-surface p-5 mb-10 flex items-center gap-2">
        <Search size={14} className="text-[color:var(--atech-text-muted)]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, reference, address, client…"
          className="bg-transparent border-none focus:outline-none text-sm flex-1 placeholder:text-[color:var(--atech-text-muted)]"
          data-testid="project-search-input"
        />
      </div>

      {err && <div className="text-[#F87171] text-sm mb-4" data-testid="projects-error">{err}</div>}

      {loading ? (
        <div className="label text-[color:var(--atech-text-muted)]">Loading projects…</div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-16 text-center">
          <div className="font-display text-2xl text-white mb-2">No projects yet</div>
          <p className="text-sm text-[color:var(--atech-text-dim)] mb-8">
            Create your first client project to start sharing documents.
          </p>
          <button
            onClick={() => setOpenCreate(true)}
            className="btn-copper inline-flex items-center gap-2 text-sm"
            data-testid="empty-state-new-project"
          >
            <Plus size={16} /> New project
          </button>
        </div>
      ) : view === "kanban" ? (
        <KanbanBoard projects={filtered} onStatusChange={handleStatusChange} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="project-grid">
          {filtered.map((p, idx) => (
            <div
              key={p.id}
              draggable={sortBy === "manual"}
              onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDragIdx(idx); }}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(idx); }}
              onDrop={(e) => { e.preventDefault(); handleGridReorder(dragIdx, overIdx); setDragIdx(null); setOverIdx(null); }}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              className={`relative group transition-all duration-150 ${
                dragIdx === idx ? "opacity-40 scale-95" : ""
              } ${
                overIdx === idx && dragIdx !== null && dragIdx !== idx
                  ? "ring-2 ring-[color:var(--atech-copper)] ring-offset-2 ring-offset-[color:var(--atech-bg)] rounded-[2px]"
                  : ""
              }`}
              data-testid={`project-card-${p.id}`}
            >
              {sortBy === "manual" && (
                <div
                  className="absolute top-3 right-3 z-10 text-[color:var(--atech-text-muted)] opacity-0 group-hover:opacity-60 cursor-grab active:cursor-grabbing transition-opacity"
                  title="Drag to reorder"
                >
                  <GripVertical size={14} />
                </div>
              )}
              <ProjectCard project={p} to={`/admin/projects/${p.id}`} />
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full py-20 text-center">
              <p className="text-sm text-[color:var(--atech-text-dim)]">No projects match your search.</p>
            </div>
          )}
        </div>
      )}

      {openCreate && (
        <CreateProjectModal
          clients={clients}
          onClose={() => setOpenCreate(false)}
          onCreated={() => { setOpenCreate(false); load(); }}
        />
      )}
    </div>
  );
}

const ATTENTION_EMPTY_COPY = {
  awaiting_reply:
    "Projects will show up here when a client uploads a document or message and A-Tech hasn't responded yet.",
  overdue:
    "Projects will show up here once they pass their due date without being completed.",
  stalled:
    "Projects will show up here when there's been no activity for 30 days or more.",
  recent_uploads:
    "Projects will show up here when a client uploads a document in the last 7 days.",
};

function AttentionEmptyState({ tileKey, onClear }) {
  return (
    <div
      className="card-surface p-12 text-center"
      data-testid={`attention-empty-${tileKey}`}
    >
      <div className="text-sm text-[color:var(--atech-text)]">
        Nothing here right now.
      </div>
      <p className="text-xs text-[color:var(--atech-text-dim)] mt-1.5 max-w-md mx-auto leading-relaxed">
        {ATTENTION_EMPTY_COPY[tileKey] || "Projects matching this filter will appear here."}
      </p>
      <button
        onClick={onClear}
        className="mt-5 text-xs text-[color:var(--atech-copper)] hover:text-[color:var(--atech-copper-hover)] transition-colors"
        data-testid="attention-empty-clear"
      >
        Clear filter
      </button>
    </div>
  );
}
